import React, { useState } from 'react';
import CalculatorLayout from '../../components/CalculatorLayout';

const ScientificCalculator = () => {
  const [display, setDisplay] = useState('0');
  const [expression, setExpression] = useState('');
  const [isRadians, setIsRadians] = useState(true);
  const [isInv, setIsInv] = useState(false);
  const [justEvaluated, setJustEvaluated] = useState(false);

  const append = (val) => {
    if (justEvaluated && /[0-9.]/.test(val)) {
      setDisplay(val); setJustEvaluated(false); return;
    }
    if (display === '0' && /[0-9.]/.test(val)) setDisplay(val);
    else setDisplay(display + val);
  };

  const clearAll = () => { setDisplay('0'); setExpression(''); setJustEvaluated(false); };
  const backspace = () => setDisplay(display.length > 1 ? display.slice(0, -1) : '0');

  const applyFn = (fn) => {
    const x = parseFloat(display);
    let res;
    const toRad = (v) => isRadians ? v : v * Math.PI / 180;
    const fromRad = (v) => isRadians ? v : v * 180 / Math.PI;
    try {
      switch (fn) {
        case 'sin': res = isInv ? fromRad(Math.asin(x)) : Math.sin(toRad(x)); break;
        case 'cos': res = isInv ? fromRad(Math.acos(x)) : Math.cos(toRad(x)); break;
        case 'tan': res = isInv ? fromRad(Math.atan(x)) : Math.tan(toRad(x)); break;
        case 'log': res = isInv ? Math.pow(10, x) : Math.log10(x); break;
        case 'ln': res = isInv ? Math.exp(x) : Math.log(x); break;
        case 'sqrt': res = isInv ? x * x : Math.sqrt(x); break;
        case 'square': res = x * x; break;
        case 'cube': res = x * x * x; break;
        case 'fact': {
          if (x < 0 || x > 170 || !Number.isInteger(x)) { res = NaN; break; }
          res = 1; for (let i = 2; i <= x; i++) res *= i; break;
        }
        case 'abs': res = Math.abs(x); break;
        case 'neg': res = -x; break;
        case 'reciprocal': res = 1 / x; break;
        case 'exp': res = Math.exp(x); break;
        default: res = x;
      }
      if (!isFinite(res) || isNaN(res)) { setDisplay('Error'); return; }
      setDisplay(String(Math.round(res * 1e10) / 1e10));
      setJustEvaluated(true);
    } catch { setDisplay('Error'); }
  };

  const evaluate = () => {
    try {
      const full = (expression + display)
        .replace(/×/g, '*').replace(/÷/g, '/')
        .replace(/π/g, String(Math.PI)).replace(/e/g, String(Math.E))
        .replace(/\^/g, '**');
      // eslint-disable-next-line no-new-func
      const res = Function(`'use strict'; return (${full})`)();
      if (!isFinite(res)) { setDisplay('Error'); return; }
      setDisplay(String(Math.round(res * 1e10) / 1e10));
      setExpression(''); setJustEvaluated(true);
    } catch { setDisplay('Error'); }
  };

  const operator = (op) => {
    setExpression(expression + display + ' ' + op + ' ');
    setDisplay('0'); setJustEvaluated(false);
  };

  const btn = (label, onClick, type = 'num') => {
    const styles = {
      num: 'bg-white border-slate-200 text-bharat-navy hover:bg-slate-50',
      fn: 'bg-slate-50 border-slate-200 text-slate-700 hover:bg-slate-100 text-sm',
      op: 'bg-slate-100 border-slate-200 text-bharat-navy hover:bg-slate-200',
      eq: 'bg-saffron-500 border-saffron-500 text-white hover:bg-saffron-600',
      toggle: 'bg-indgreen-50 border-indgreen-200 text-indgreen-700 hover:bg-indgreen-100 text-xs',
      active: 'bg-indgreen-500 border-indgreen-500 text-white text-xs',
    };
    return (
      <button onClick={onClick} className={`calc-btn h-11 md:h-12 rounded-lg border font-medium flex items-center justify-center ${styles[type]}`}>
        {label}
      </button>
    );
  };

  return (
    <CalculatorLayout
      title="Scientific Calculator"
      subtitle="Advanced calculator with trig, logs, exponents and memory."
      category="Math"
      description={<p>A full-featured scientific calculator supporting trigonometric functions (sin/cos/tan and their inverses), logarithms (log, ln), exponents, factorials, and constants like π and e. Toggle between radians and degrees, and use <strong>INV</strong> to access inverse functions.</p>}
    >
      <div className="max-w-2xl mx-auto">
        {/* Display */}
        <div className="bg-gradient-to-br from-slate-50 to-white rounded-xl border border-slate-200 p-4 mb-4">
          <div className="flex items-center justify-between text-xs text-slate-400 mb-1">
            <div className="flex gap-1.5">
              <span className={`px-2 py-0.5 rounded text-[10px] font-semibold ${isRadians ? 'bg-indgreen-100 text-indgreen-700' : 'bg-slate-100'}`}>{isRadians ? 'RAD' : 'DEG'}</span>
              {isInv && <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-saffron-100 text-saffron-700">INV</span>}
            </div>
            <span className="display-font truncate max-w-[70%]">{expression}</span>
          </div>
          <div className="display-font text-right text-3xl md:text-4xl font-semibold text-bharat-navy truncate">{display}</div>
        </div>

        <div className="grid grid-cols-6 gap-1.5">
          {btn(isRadians ? 'RAD' : 'DEG', () => setIsRadians(!isRadians), isRadians ? 'active' : 'toggle')}
          {btn('INV', () => setIsInv(!isInv), isInv ? 'active' : 'toggle')}
          {btn('(', () => append('('), 'fn')}
          {btn(')', () => append(')'), 'fn')}
          {btn('AC', clearAll, 'fn')}
          {btn('⌫', backspace, 'fn')}

          {btn(isInv ? 'sin⁻¹' : 'sin', () => applyFn('sin'), 'fn')}
          {btn(isInv ? 'cos⁻¹' : 'cos', () => applyFn('cos'), 'fn')}
          {btn(isInv ? 'tan⁻¹' : 'tan', () => applyFn('tan'), 'fn')}
          {btn(isInv ? '10ˣ' : 'log', () => applyFn('log'), 'fn')}
          {btn(isInv ? 'eˣ' : 'ln', () => applyFn('ln'), 'fn')}
          {btn('x!', () => applyFn('fact'), 'fn')}

          {btn('x²', () => applyFn('square'), 'fn')}
          {btn('x³', () => applyFn('cube'), 'fn')}
          {btn('x^y', () => append('^'), 'fn')}
          {btn(isInv ? 'x²' : '√', () => applyFn('sqrt'), 'fn')}
          {btn('π', () => append('π'), 'fn')}
          {btn('e', () => append('e'), 'fn')}

          {btn('7', () => append('7'))}
          {btn('8', () => append('8'))}
          {btn('9', () => append('9'))}
          {btn('÷', () => operator('÷'), 'op')}
          {btn('1/x', () => applyFn('reciprocal'), 'fn')}
          {btn('|x|', () => applyFn('abs'), 'fn')}

          {btn('4', () => append('4'))}
          {btn('5', () => append('5'))}
          {btn('6', () => append('6'))}
          {btn('×', () => operator('×'), 'op')}
          {btn('±', () => applyFn('neg'), 'fn')}
          {btn('%', () => setDisplay(String(parseFloat(display) / 100)), 'fn')}

          {btn('1', () => append('1'))}
          {btn('2', () => append('2'))}
          {btn('3', () => append('3'))}
          {btn('−', () => operator('-'), 'op')}
          <div className="col-span-2 row-span-2">
            <button onClick={evaluate} className="w-full h-full calc-btn rounded-lg bg-saffron-500 hover:bg-saffron-600 text-white font-display text-2xl font-bold">=</button>
          </div>

          {btn('0', () => append('0'))}
          {btn('.', () => append('.'))}
          {btn('EXP', () => append('e'), 'fn')}
          {btn('+', () => operator('+'), 'op')}
        </div>
      </div>
    </CalculatorLayout>
  );
};

export default ScientificCalculator;
